// i18n/index.js — i18next bootstrap for the iYuga web app.
//
// ─────────────────────────────────────────────────────────────────────────────
// FLAG-OFF / ADDITIVE — this file changes NOTHING about what users see today.
// English is the one and only bundled language and the hard fallback, so every
// `t('key')` resolves to the exact English literal the surface used before.
// There is no language detection, no remote fetch, no extra network/render work
// unless a maintainer explicitly turns it on (see I18N_DETECT below).
//
// This is the "build every NEW surface i18n-ready, defer the big retrofit"
// foundation from ROADMAP.md → "Internationalization / Google Translate (i18n)".
// ─────────────────────────────────────────────────────────────────────────────
//
// ASSUMPTION (dev/Gordon to confirm): framework = react-i18next, not the cheap
// Google-Translate widget. See the PR body + i18n/README.md.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// English baseline namespaces. Adding a language later = drop a sibling locale
// folder (e.g. locales/es/*.json) and register it here / lazy-load it — no
// surface code changes.
import enCommon from './locales/en/common.json';
import enConsent from './locales/en/consent.json';
import enStorage from './locales/en/storage.json';
import enAuth from './locales/en/auth.json';
import enGallery from './locales/en/gallery.json';
import enWheelview from './locales/en/wheelview.json';

export const DEFAULT_LANGUAGE = 'en';

// Namespaces. `common` is the default; surfaces use a focused namespace.
export const NAMESPACES = ['common', 'consent', 'storage', 'auth', 'gallery', 'wheelview'];

export const resources = {
  en: {
    common: enCommon,
    consent: enConsent,
    storage: enStorage,
    auth: enAuth,
    gallery: enGallery,
    wheelview: enWheelview,
  },
};

// Language detection is OFF by default so today's behavior is byte-identical
// (everyone gets English). A maintainer can flip this ON — and add
// `i18next-browser-languagedetector` to the chain — once translated locales
// actually exist and the dev has confirmed the framework choice.
const I18N_DETECT = process.env.REACT_APP_I18N_DETECT === 'true';

i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE, // pin to English; no auto-detect today
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: 'common',
  ns: NAMESPACES,
  // If a key is missing for any reason, render the key's English default text
  // rather than a raw key — keeps the UI safe even mid-extraction.
  returnEmptyString: false,
  interpolation: {
    escapeValue: false, // React already escapes; values are app-controlled.
  },
  react: {
    useSuspense: false, // resources are bundled synchronously — no Suspense.
  },
  // Quiet in production; surfaces a clear log in dev if a key is ever missing.
  debug: false,
  saveMissing: false,
});

// Opt-in only. Detection stays dormant unless a maintainer turns it on AND
// wires the detector dependency in. Documented here so it's discoverable.
if (I18N_DETECT) {
  // eslint-disable-next-line no-console
  console.info(
    '[i18n] REACT_APP_I18N_DETECT=true — language detection is enabled. ' +
      'Ensure i18next-browser-languagedetector is wired before relying on it.'
  );
}

export default i18n;
